/**
 * Hook for managing photo reordering with drag-and-drop
 *
 * @module usePhotoReorder
 */

import { useState, useCallback, useMemo } from 'react';
import type {
  DragStartEvent,
  DragEndEvent,
  DragOverEvent,
} from '@dnd-kit/core';
import type { PhotoItem2ViewModel } from '../../PhotoItem2/PhotoItem2.types';
import type { ReorderResult, ReorderState, ReorderContext } from '../PhotoCollection2.types';
import { reorderArray } from '../../../lib/utils/reorder';
import logger from '../../../utils/logger';

export interface UsePhotoReorderProps {
  /**
   * Array of photo items
   */
  items: PhotoItem2ViewModel[];

  /**
   * Callback when reorder completes
   */
  onReorder?: (result: ReorderResult) => void | Promise<void> | boolean;

  /**
   * Whether reorder mode is enabled
   */
  enabled: boolean;
}

export interface UsePhotoReorderReturn {
  /**
   * Reorder context to pass to layouts
   */
  reorderContext: ReorderContext | undefined;

  /**
   * DnD Kit event handlers
   */
  handlers: {
    onDragStart: (event: DragStartEvent) => void;
    onDragEnd: (event: DragEndEvent) => void;
    onDragOver: (event: DragOverEvent) => void;
    onDragCancel: () => void;
  };

  /**
   * Currently active drag ID (string, used directly by DnD Kit)
   */
  activeId: string | null;

  /**
   * Whether a drag is in progress
   */
  isDragging: boolean;
}

/**
 * Custom hook for managing photo reordering
 *
 * Handles drag events, state management, and callback invocation.
 *
 * @param props - Hook configuration
 * @returns Reorder context and event handlers
 */
export function usePhotoReorder({
  items,
  onReorder,
  enabled,
}: UsePhotoReorderProps): UsePhotoReorderReturn {
  const [reorderState, setReorderState] = useState<ReorderState>({
    activeId: null,
    currentIndex: null,
    originalIndex: null,
    isDragging: false,
    dragStartTime: null,
  });

  // Create array of item IDs for SortableContext (already strings)
  const itemIds = useMemo(() => items.map((item) => item.photoData.id), [items]);

  // Handler to move item up one position
  const handleMoveUp = useCallback(
    (id: string) => {
      const currentIndex = items.findIndex((item) => item.photoData.id === id);
      if (currentIndex <= 0) return; // Already at top

      const newIndex = currentIndex - 1;
      const reordered = reorderArray(items, currentIndex, newIndex);

      // Call onReorder callback
      if (onReorder) {
        const result: ReorderResult = {
          reorderedItems: reordered,
          movedItem: items[currentIndex],
          oldIndex: currentIndex,
          newIndex,
          timestamp: new Date().toISOString(),
        };

        onReorder(result);
      }
    },
    [items, onReorder]
  );

  // Handler to move item down one position
  const handleMoveDown = useCallback(
    (id: string) => {
      const currentIndex = items.findIndex((item) => item.photoData.id === id);
      if (currentIndex === -1 || currentIndex >= items.length - 1) return; // Already at bottom

      const newIndex = currentIndex + 1;
      const reordered = reorderArray(items, currentIndex, newIndex);

      // Call onReorder callback
      if (onReorder) {
        const result: ReorderResult = {
          reorderedItems: reordered,
          movedItem: items[currentIndex],
          oldIndex: currentIndex,
          newIndex,
          timestamp: new Date().toISOString(),
        };

        onReorder(result);
      }
    },
    [items, onReorder]
  );

  // Create reorder context for layouts
  const reorderContext: ReorderContext | undefined = useMemo(() => {
    if (!enabled) return undefined;

    return {
      enabled: true,
      activeId: reorderState.activeId,
      items: itemIds,
      onMoveUp: handleMoveUp,
      onMoveDown: handleMoveDown,
    };
  }, [enabled, reorderState.activeId, itemIds, handleMoveUp, handleMoveDown]);

  // Drag start handler
  const handleDragStart = useCallback((event: DragStartEvent) => {
    const { active } = event;
    const activeId = active.id as string;

    // Validate active ID type (security hardening)
    if (typeof activeId !== 'string') {
      logger.error('PhotoReorder', 'Invalid active ID type:', typeof activeId);
      return;
    }

    const originalIndex = items.findIndex((item) => item.photoData.id === activeId);

    // Validate that item was found
    if (originalIndex === -1) {
      logger.error('PhotoReorder', 'Item not found:', activeId);
      return;
    }

    setReorderState({
      activeId,
      currentIndex: originalIndex,
      originalIndex,
      isDragging: true,
      dragStartTime: Date.now(),
    });
  }, [items]);

  // Drag over handler (for visual feedback)
  const handleDragOver = useCallback((event: DragOverEvent) => {
    const { active, over } = event;

    if (!over || active.id === over.id) return;

    const overId = over.id as string;

    setReorderState((prev) => {
      if (!prev.isDragging) return prev;

      const overIndex = items.findIndex((item) => item.photoData.id === overId);

      return {
        ...prev,
        currentIndex: overIndex,
      };
    });
  }, [items]);

  // Drag end handler
  const handleDragEnd = useCallback(
    async (event: DragEndEvent) => {
      const { active, over } = event;

      // Reset drag state
      const dragDuration = reorderState.dragStartTime
        ? Date.now() - reorderState.dragStartTime
        : 0;

      setReorderState({
        activeId: null,
        currentIndex: null,
        originalIndex: null,
        isDragging: false,
        dragStartTime: null,
      });

      // If dropped outside or no movement, do nothing
      if (!over || active.id === over.id) {
        logger.info('PhotoReorder', 'Drag cancelled or no movement');
        return;
      }

      const activeId = active.id as string;
      const overId = over.id as string;

      const oldIndex = items.findIndex((item) => item.photoData.id === activeId);
      const newIndex = items.findIndex((item) => item.photoData.id === overId);

      if (oldIndex === -1 || newIndex === -1) {
        logger.error('PhotoReorder', 'Invalid drag indices', { oldIndex, newIndex });
        return;
      }

      // Reorder array
      const reordered = reorderArray(items, oldIndex, newIndex);

      // Log performance
      logger.info('PhotoReorder', 'Drag completed', {
        movedItemId: activeId,
        oldIndex,
        newIndex,
        duration: `${dragDuration}ms`,
      });

      // Call onReorder callback
      if (onReorder) {
        try {
          const result: ReorderResult = {
            reorderedItems: reordered,
            movedItem: items[oldIndex],
            oldIndex,
            newIndex,
            timestamp: new Date().toISOString(),
          };

          const callbackResult = await onReorder(result);

          // If callback returns false explicitly, log warning
          if (callbackResult === false) {
            logger.warn('PhotoReorder', 'Reorder was cancelled by callback');
          }
        } catch (error) {
          logger.error('PhotoReorder', 'onReorder callback failed:', error);
          // Note: Parent component should handle error UI
        }
      } else {
        logger.warn('PhotoReorder', 'Drag completed but no onReorder callback provided');
      }
    },
    [items, onReorder, reorderState.dragStartTime]
  );

  // Drag cancel handler
  const handleDragCancel = useCallback(() => {
    logger.info('PhotoReorder', 'Drag cancelled');

    setReorderState({
      activeId: null,
      currentIndex: null,
      originalIndex: null,
      isDragging: false,
      dragStartTime: null,
    });
  }, []);

  return {
    reorderContext,
    handlers: {
      onDragStart: handleDragStart,
      onDragEnd: handleDragEnd,
      onDragOver: handleDragOver,
      onDragCancel: handleDragCancel,
    },
    activeId: reorderState.activeId,
    isDragging: reorderState.isDragging,
  };
}
